import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => {
    const configurationCompleted = state.pages.productDetails.configurationCompleted;
    const variantSelectionCompleted = state.pages.productDetails.variantSelectionCompleted;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            ownProps.productContext.product,
            ownProps.productContext.productInfo,
            configurationCompleted,
            variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, ownProps.productContext),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface GAIAProductListControlsStyles {
    container?: InjectableCss;
    quantityContainer?: InjectableCss;
    addToCartBtnContainer?: InjectableCss;
    addToListContainer?: InjectableCss;
    paginationContainer?: InjectableCss;
}

export const styles: GAIAProductListControlsStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 1rem 0rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            row-gap: 5px;
                            flex-wrap: wrap;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    quantityContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    addToCartBtnContainer: {
        css: css`
            margin-right: 15px;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 1;
                        margin-right: 0;
                    `,
                    css`
                        width: 57%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    addToListContainer: {
        css: css`
            > div[class^="Wrapper"] > div[class^="sc"] {
                margin-top: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const GAIAProductDetailsActions: FC<Props> = ({ id, productSettings, canAddToCart, hasEnoughInventory }) => {
    return (
        <StyledWrapper {...styles.container}>
            {productSettings.canAddToCart && (
                <StyledWrapper {...styles.quantityContainer}>
                    <Zone contentId={id} zoneName="QuantityContainer" />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.addToCartBtnContainer}>
                <Zone contentId={id} zoneName="AddToCartBtnContainer" />
            </StyledWrapper>
            <StyledWrapper {...styles.addToListContainer}>
                <Zone contentId={id} zoneName="AddToListContainer" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(GAIAProductDetailsActions)),
    definition: {
        group: "Product Details",
        displayName: "GAIA Product Details Controls",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
